import { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Image as ImageIcon,
  LoaderCircle,
  Play,
  RefreshCcw,
  Shuffle,
  SquareArrowOutUpRight,
  X,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { getSections, getStationById, getStations, type ApiPayloadResult } from '../../lib/api'
import { learnerStationLabel } from '../../lib/learnerLabels'
import type { Answer, Section, Station, StationImage, Subquestion } from '../../lib/types'
import styles from './exam.module.css'

export interface ExamRouteProps {
  /** Passing both arrays makes the route controlled; otherwise it loads API data with the shared seed fallback. */
  stations?: Station[]
  sections?: Section[]
}

export interface ExamQuestion {
  id: string
  prompt: string
  answers: Answer[]
  order: number
}

/** One card represents one station, so the builder's count always means stations. */
export interface ExamCard {
  id: string
  stationId: string
  sectionId: string
  sectionName: string
  stationTitle: string
  stationOrder: number
  prompt: string
  answers: Answer[]
  questions: ExamQuestion[]
  images: StationImage[]
}

export interface ExamBuilderInput {
  sectionIds: string[]
  count: number
  imagesOnly: boolean
  shuffle: boolean
}

export interface ExamRunnerProps {
  cards: ExamCard[]
  onClose: () => void
  onRestart: () => void
}

interface ExamContentState {
  status: 'loading' | 'ready' | 'error'
  stations: Station[]
  sections: Section[]
  source?: 'api' | 'seed'
  warning?: string
  error?: string
}

type StationWithCounts = Station & {
  questionCount?: number
  mediaCount?: number
}

function cloneArray<T>(value: T[]): T[] {
  return value.slice()
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, Math.floor(value)))
}

function normalizeCount(value: number, maxCount: number): number {
  return maxCount === 0 ? 0 : clamp(value, 1, maxCount)
}

function shuffleItems<T>(input: T[]): T[] {
  const output = [...input]
  for (let index = output.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1))
    const item = output[index]
    output[index] = output[randomIndex]
    output[randomIndex] = item
  }
  return output
}

function getSectionsById(sections: Section[]): Map<string, Section> {
  return new Map(sections.filter((section) => section.id.trim()).map((section) => [section.id, section]))
}

function resolveSectionName(sectionId: string, sectionsById: Map<string, Section>): string {
  return sectionsById.get(sectionId)?.name ?? 'قسم غير محدد'
}

function orderedQuestions(station: Station): Subquestion[] {
  return [...station.subquestions].sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
}

export function buildExamCards(stations: Station[], sections: Section[] = []): ExamCard[] {
  const sectionById = getSectionsById(sections)
  return stations.map((station) => {
    const questions = orderedQuestions(station).map((question) => ({
      id: question.id,
      prompt: question.prompt.trim() || 'راجع النقاط الأساسية في هذه المحطة.',
      order: question.order,
      answers: [...question.answers].sort((left, right) => left.order - right.order || left.id.localeCompare(right.id)),
    }))
    const answers = questions.flatMap((question) => question.answers)

    return {
      id: station.id,
      stationId: station.id,
      sectionId: station.sectionId,
      sectionName: resolveSectionName(station.sectionId, sectionById),
      stationTitle: learnerStationLabel(station.id, station.order),
      stationOrder: station.order,
      prompt: station.scenario?.trim() || 'راجع محتوى هذه المحطة ثم أجب عن أسئلتها.',
      answers,
      questions,
      images: [...station.images],
    }
  })
}

function getKnownSectionIds(stations: Station[], sections: Section[]): string[] {
  const ids = [...sections.map((section) => section.id), ...stations.map((station) => station.sectionId)]
  return [...new Set(ids.filter((id) => id.trim()))]
}

function isEligibleStation(station: Station): boolean {
  // The learner API only returns published stations. This guard prevents an accidentally
  // supplied draft or archived station from leaking into a normal exam session.
  return station.status === 'published'
}

function stationNeedsDetails(station: Station): boolean {
  const withCounts = station as StationWithCounts
  const hasMissingQuestions = station.subquestions.length === 0 && (withCounts.questionCount ?? 0) > 0
  const hasMissingMedia = station.images.length === 0 && (withCounts.mediaCount ?? 0) > 0
  return hasMissingQuestions || hasMissingMedia
}

async function hydrateStationDetails(stations: Station[], signal: AbortSignal): Promise<Station[]> {
  const candidates = stations.filter(stationNeedsDetails)
  if (candidates.length === 0) return stations

  const resolved = new Map<string, Station>()
  // Keep the detail requests bounded: the list endpoint is intentionally light,
  // while the runner needs complete question and media documents.
  for (let index = 0; index < candidates.length; index += 8) {
    const batch = candidates.slice(index, index + 8)
    const results = await Promise.all(batch.map(async (station) => {
      try {
        const result = await getStationById(station.id, signal)
        return result.source === 'api' && result.data ? result.data : null
      } catch {
        return null
      }
    }))
    for (const station of results) {
      if (station) resolved.set(station.id, station)
    }
  }

  return stations.map((station) => resolved.get(station.id) ?? station)
}

export function filterStations(stations: Station[], settings: ExamBuilderInput, sectionIds: string[]): Station[] {
  const selected = settings.sectionIds.length === 0 ? sectionIds : settings.sectionIds
  const selectedSet = new Set(selected)
  return stations.filter((station) => {
    if (!isEligibleStation(station) || !selectedSet.has(station.sectionId)) return false
    return !settings.imagesOnly || station.images.length > 0
  })
}

function useExamContent(stationsProp: Station[] | undefined, sectionsProp: Section[] | undefined, reloadToken: number): ExamContentState {
  const isControlled = stationsProp !== undefined && sectionsProp !== undefined
  const [state, setState] = useState<ExamContentState>(() => (
    isControlled
      ? { status: 'ready', stations: cloneArray(stationsProp ?? []), sections: cloneArray(sectionsProp ?? []), source: 'api' }
      : { status: 'loading', stations: [], sections: [] }
  ))

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    if (isControlled) {
      setState({
        status: 'ready',
        stations: cloneArray(stationsProp ?? []),
        sections: cloneArray(sectionsProp ?? []),
        source: 'api',
      })
      return () => {
        cancelled = true
      }
    }

    setState({ status: 'loading', stations: [], sections: [] })
    const sectionsRequest = sectionsProp === undefined
      ? getSections(controller.signal)
      : Promise.resolve({ data: sectionsProp, source: 'api' as const } as ApiPayloadResult<Section[]>)
    const stationsRequest = stationsProp === undefined
      ? getStations({}, controller.signal)
      : Promise.resolve({ data: stationsProp, source: 'api' as const } as ApiPayloadResult<Station[]>)

    Promise.all([sectionsRequest, stationsRequest])
      .then(async ([sectionsResult, stationsResult]) => {
        if (cancelled) return
        const completeStations = stationsResult.source === 'api'
          ? await hydrateStationDetails(stationsResult.data, controller.signal)
          : stationsResult.data
        if (cancelled) return
        const source = sectionsResult.source === 'api' && stationsResult.source === 'api' ? 'api' : 'seed'
        setState({
          status: 'ready',
          sections: sectionsResult.data,
          stations: completeStations,
          source,
          warning: sectionsResult.warning ?? stationsResult.warning,
        })
      })
      .catch((reason: unknown) => {
        if (cancelled) return
        setState({
          status: 'error',
          sections: [],
          stations: [],
          error: reason instanceof Error ? reason.message : 'تعذر تحميل بيانات الاختبار.',
        })
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [isControlled, reloadToken, sectionsProp, stationsProp])

  return state
}

export function useExamBuilderState(stations: Station[], sections: Section[], initialCount = 10) {
  const sectionById = useMemo(() => getSectionsById(sections), [sections])
  const sectionIds = useMemo(() => getKnownSectionIds(stations, sections), [sections, stations])
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([])
  const [count, setCount] = useState(initialCount)
  const [imagesOnly, setImagesOnly] = useState(false)
  const [shuffle, setShuffle] = useState(true)

  const selectedIds = useMemo(
    () => selectedSectionIds.filter((id) => sectionIds.includes(id)),
    [sectionIds, selectedSectionIds],
  )
  const resolvedSectionIds = useMemo(
    () => new Set(selectedIds.length === 0 ? sectionIds : selectedIds),
    [selectedIds, sectionIds],
  )
  const settingsForFiltering = useMemo<ExamBuilderInput>(() => ({
    sectionIds: selectedIds,
    count,
    imagesOnly,
    shuffle,
  }), [count, imagesOnly, selectedIds, shuffle])
  const eligibleStations = useMemo(
    () => filterStations(stations, settingsForFiltering, sectionIds),
    [sectionIds, settingsForFiltering, stations],
  )
  const maxCount = eligibleStations.length
  const safeCount = useMemo(() => normalizeCount(count, maxCount), [count, maxCount])

  useEffect(() => {
    if (maxCount === 0) {
      if (count !== 0) setCount(0)
      return
    }
    const next = normalizeCount(count, maxCount)
    if (next !== count) setCount(next)
  }, [count, maxCount])

  function toggleSection(sectionId: string) {
    setSelectedSectionIds((previous) => {
      if (previous.length === 0) return [sectionId]
      if (previous.includes(sectionId)) return previous.filter((id) => id !== sectionId)
      return [...previous, sectionId]
    })
  }

  function selectAllSections() {
    setSelectedSectionIds([])
  }

  function reset() {
    setSelectedSectionIds([])
    setCount(maxCount > 0 ? Math.min(initialCount, maxCount) : 0)
    setImagesOnly(false)
    setShuffle(true)
  }

  function buildSessionCards(): { cards: ExamCard[]; stationIds: string[] } {
    const baseStations = filterStations(stations, {
      sectionIds: selectedIds,
      count: safeCount,
      imagesOnly,
      shuffle,
    }, sectionIds)
    const ordered = [...baseStations].sort((left, right) => (
      left.order - right.order || left.title.localeCompare(right.title, 'ar')
    ))
    const selectedStations = (shuffle ? shuffleItems(ordered) : ordered).slice(0, safeCount)
    return {
      cards: buildExamCards(selectedStations, sections),
      stationIds: selectedStations.map((station) => station.id),
    }
  }

  return {
    sectionById,
    sectionIds,
    resolvedSectionIds,
    eligibleStations,
    maxCount,
    safeCount,
    selectedSectionIds: selectedIds,
    count,
    setCount,
    imagesOnly,
    setImagesOnly,
    shuffle,
    setShuffle,
    hasAvailableStations: maxCount > 0,
    toggleSection,
    selectAllSections,
    reset,
    buildSessionCards,
  }
}

function questionKey(card: ExamCard, questionId: string): string {
  return `${card.id}:${questionId}`
}

function formatProgressDate(value: string | undefined): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('ar-EG')
}

export function ExamRunner({ cards, onClose, onRestart }: ExamRunnerProps) {
  const [activeCardIndex, setActiveCardIndex] = useState(0)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [revealed, setRevealed] = useState<Record<string, boolean>>({})
  const [finished, setFinished] = useState(false)

  useEffect(() => {
    setActiveCardIndex(0)
    setActiveImageIndex(0)
    setRevealed({})
    setFinished(false)
  }, [cards])

  useEffect(() => {
    setActiveImageIndex(0)
  }, [activeCardIndex])

  const activeCard = cards[activeCardIndex]
  const totalQuestions = cards.reduce((sum, card) => sum + card.questions.length, 0)
  const revealCount = Object.values(revealed).filter(Boolean).length
  const completionRate = totalQuestions === 0 ? 0 : Math.round((revealCount / totalQuestions) * 100)
  const activeImage = activeCard?.images[activeImageIndex]
  const activeQuestions = activeCard?.questions ?? []

  function toggleQuestion(questionId: string) {
    const key = activeCard ? questionKey(activeCard, questionId) : questionId
    setRevealed((previous) => ({ ...previous, [key]: !previous[key] }))
  }

  function revealAllQuestions() {
    if (!activeCard || activeQuestions.length === 0) return
    const allRevealed = activeQuestions.every((question) => revealed[questionKey(activeCard, question.id)])
    setRevealed((previous) => {
      const next = { ...previous }
      for (const question of activeQuestions) next[questionKey(activeCard, question.id)] = !allRevealed
      return next
    })
  }

  function goNext() {
    if (activeCardIndex >= cards.length - 1) {
      setFinished(true)
      return
    }
    setActiveCardIndex((value) => value + 1)
  }

  if (cards.length === 0) {
    return (
      <section className={styles.examState} aria-live="polite">
        <h2>لا توجد محطات تطابق اختياراتك</h2>
        <p>اختر أقسامًا أخرى أو ألغِ خيار «محطات تحتوي على صور فقط» ثم حاول مرة أخرى.</p>
        <button type="button" onClick={onRestart}>تعديل الإعدادات</button>
      </section>
    )
  }

  if (finished) {
    return (
      <main className={styles.examRunner} dir="rtl" aria-label="نتيجة الاختبار السريع">
        <section className={styles.completePanel} aria-live="polite">
          <CheckCircle2 size={30} aria-hidden="true" />
          <h1>أكملت الاختبار</h1>
          <p>راجعت {cards.length} محطة وكشفت إجابات {revealCount} من أصل {totalQuestions} سؤالًا.</p>
          <div className={styles.completePanelActions}>
            <button type="button" onClick={onRestart}><RefreshCcw size={15} aria-hidden="true" /> إنشاء اختبار جديد</button>
            <button type="button" onClick={onClose}><X size={15} aria-hidden="true" /> العودة للإعدادات</button>
          </div>
        </section>
      </main>
    )
  }

  if (!activeCard) return null

  const currentQuestionKeys = new Set(activeQuestions.map((question) => questionKey(activeCard, question.id)))
  const activeRevealedCount = [...currentQuestionKeys].filter((key) => revealed[key]).length
  const allCurrentRevealed = activeQuestions.length > 0 && activeRevealedCount === activeQuestions.length
  const previousImage = () => setActiveImageIndex((value) => (value - 1 + activeCard.images.length) % activeCard.images.length)
  const nextImage = () => setActiveImageIndex((value) => (value + 1) % activeCard.images.length)

  return (
    <main className={styles.examRunner} dir="rtl" aria-label="جولة الاختبار السريع">
      <header className={styles.runnerHeader}>
        <div className={styles.runnerHeading}>
          <p className={styles.eyebrow}>اختبار سريع</p>
          <h1>{activeCard.stationTitle}</h1>
          <p>{activeCard.sectionName}</p>
        </div>
        <div className={styles.runnerHeaderActions}>
          <Link to={`/stations/${encodeURIComponent(activeCard.stationId)}`} className={styles.stationLink}>
            فتح المحطة <SquareArrowOutUpRight size={15} aria-hidden="true" />
          </Link>
          <button type="button" onClick={onClose}>إنهاء الجلسة</button>
        </div>
      </header>

      <div className={styles.runnerStats} aria-live="polite">
        <span>محطة {activeCardIndex + 1} من {cards.length}</span>
        <span>كشفت {completionRate}% من الإجابات</span>
      </div>

      <nav className={styles.cardNavigation} aria-label="التنقل بين محطات الاختبار">
        <ol>
          {cards.map((card, index) => {
            const cardQuestions = card.questions.map((question) => questionKey(card, question.id))
            const cardComplete = cardQuestions.length === 0 || cardQuestions.every((key) => revealed[key])
            return (
              <li key={card.id}>
                <button
                  type="button"
                  className={index === activeCardIndex ? styles.cardNavActive : styles.cardNavButton}
                  aria-current={index === activeCardIndex ? 'step' : undefined}
                  aria-label={`الانتقال إلى المحطة ${index + 1}: ${card.stationTitle}`}
                  onClick={() => setActiveCardIndex(index)}
                >
                  <span>{index + 1}</span>
                  {cardComplete && <CheckCircle2 size={13} aria-label="مكتملة" />}
                </button>
              </li>
            )
          })}
        </ol>
      </nav>

      <article className={styles.questionCard}>
        <div className={styles.cardIntro}>
          <div>
            <p className={styles.cardLabel}>السيناريو</p>
            <p className={styles.questionPrompt}>{activeCard.prompt}</p>
          </div>
          <span className={styles.questionCount}>{activeQuestions.length} سؤال فرعي</span>
        </div>

        <section className={styles.imagePanel} aria-label="صور المحطة">
          {activeImage ? (
            <figure>
              <img src={activeImage.key} alt={`صورة ${activeCard.stationTitle}`} />
              <figcaption>الصورة {activeImageIndex + 1} من {activeCard.images.length}</figcaption>
            </figure>
          ) : (
            <div className={styles.noImage} role="img" aria-label="لا توجد صورة لهذه المحطة">
              لا تحتوي هذه المحطة على صور
            </div>
          )}
          {activeCard.images.length > 1 && (
            <div className={styles.imageControls}>
              <button type="button" onClick={previousImage} aria-label="الصورة السابقة"><ChevronRight size={17} /> السابقة</button>
              <button type="button" onClick={nextImage} aria-label="الصورة التالية">التالية <ChevronLeft size={17} /></button>
            </div>
          )}
        </section>

        <section className={styles.questionsSection} aria-labelledby="exam-questions-title">
          <div className={styles.questionsHeading}>
            <h2 id="exam-questions-title">الأسئلة الفرعية</h2>
            {activeQuestions.length > 0 && (
              <button type="button" className={styles.secondaryButton} onClick={revealAllQuestions}>
                <Eye size={15} aria-hidden="true" />
                {allCurrentRevealed ? 'إخفاء كل الإجابات' : 'كشف كل الإجابات'}
              </button>
            )}
          </div>
          {activeQuestions.length === 0 ? (
            <p className={styles.noQuestions}>لم تُضف أسئلة إلى هذه المحطة بعد.</p>
          ) : (
            <ol className={styles.questionList}>
              {activeQuestions.map((question, index) => {
                const key = questionKey(activeCard, question.id)
                const isRevealed = Boolean(revealed[key])
                const answersId = `exam-answers-${encodeURIComponent(key)}`
                return (
                  <li key={question.id} className={styles.subquestion}>
                    <div className={styles.subquestionHeader}>
                      <h3>سؤال {index + 1}: {question.prompt}</h3>
                      <button
                        type="button"
                        className={styles.revealButton}
                        aria-expanded={isRevealed}
                        aria-controls={answersId}
                        onClick={() => toggleQuestion(question.id)}
                      >
                        {isRevealed ? 'إخفاء الإجابة' : 'كشف الإجابة'}
                      </button>
                    </div>
                    {isRevealed && (
                      <div id={answersId} className={styles.answerZone}>
                        {question.answers.length === 0 ? (
                          <p>لم تُضف إجابة لهذا السؤال بعد.</p>
                        ) : (
                          <ol>
                            {question.answers.map((answer) => <li key={answer.id}>{answer.body}</li>)}
                          </ol>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </section>
      </article>

      <footer className={styles.runnerControls}>
        <button type="button" onClick={() => setActiveCardIndex((value) => value - 1)} disabled={activeCardIndex === 0}>
          <ChevronRight size={17} aria-hidden="true" /> السابق
        </button>
        <button type="button" onClick={goNext} className={styles.nextButton}>
          {activeCardIndex >= cards.length - 1 ? 'إنهاء الجولة' : 'المحطة التالية'}
          <ChevronLeft size={17} aria-hidden="true" />
        </button>
      </footer>
    </main>
  )
}

export function ExamRoute({ stations: stationsProp, sections: sectionsProp }: ExamRouteProps = {}) {
  const [reloadToken, setReloadToken] = useState(0)
  const content = useExamContent(stationsProp, sectionsProp, reloadToken)
  const builder = useExamBuilderState(content.stations, content.sections, 10)
  const [sessionCards, setSessionCards] = useState<ExamCard[] | null>(null)

  const sectionOptions = useMemo(() => {
    const byId = new Map(content.sections.map((section) => [section.id, section]))
    for (const station of content.stations) {
      if (!byId.has(station.sectionId)) {
        byId.set(station.sectionId, {
          id: station.sectionId,
          name: 'قسم غير محدد',
          slug: station.sectionId,
          order: Number.MAX_SAFE_INTEGER,
        })
      }
    }
    return [...byId.values()].sort((left, right) => left.order - right.order || left.name.localeCompare(right.name, 'ar'))
  }, [content.sections, content.stations])

  if (content.status === 'loading') {
    return (
      <main className={styles.examState} dir="rtl" aria-live="polite">
        <LoaderCircle className={styles.spin} size={25} aria-hidden="true" />
        <h1>جارٍ تجهيز الاختبار</h1>
        <p>جارٍ تحميل المحطات والأقسام المتاحة.</p>
      </main>
    )
  }

  if (content.status === 'error') {
    return (
      <main className={styles.examState} dir="rtl" aria-live="polite">
        <h1>تعذر تحميل بيانات الاختبار</h1>
        <p>{content.error}</p>
        <button type="button" onClick={() => setReloadToken((value) => value + 1)}>إعادة المحاولة</button>
      </main>
    )
  }

  if (sessionCards) {
    return <ExamRunner cards={sessionCards} onClose={() => setSessionCards(null)} onRestart={() => setSessionCards(null)} />
  }

  function startExam() {
    const next = builder.buildSessionCards()
    if (next.cards.length > 0) setSessionCards(next.cards)
  }

  return (
    <main className={styles.examPage} dir="rtl" aria-label="إعداد الاختبار السريع">
      <header className={styles.examHeader}>
        <div>
          <p className={styles.eyebrow}>اختبار سريع</p>
          <h1>أنشئ اختبارك</h1>
          <p>اختر الأقسام وعدد المحطات التي تريد مراجعتها، ثم ابدأ الاختبار.</p>
        </div>
        <button type="button" className={styles.resetButton} onClick={builder.reset}>إعادة الاختيارات للوضع الافتراضي</button>
      </header>

      {content.warning && (
        <p className={styles.warning} role="status">
          {content.warning} {content.source === 'seed' ? 'نعرض نسخة تجريبية من المحتوى حاليًا.' : ''}
        </p>
      )}

      <section className={styles.builderCard} aria-label="إعدادات الاختبار">
        <fieldset className={styles.fieldGroup}>
          <legend className={styles.fieldLabel}>الأقسام</legend>
          <div className={styles.checkboxGrid}>
            <label className={styles.checkboxField}>
              <input type="checkbox" checked={builder.selectedSectionIds.length === 0} onChange={builder.selectAllSections} />
              <span>كل الأقسام</span>
            </label>
            {sectionOptions.length === 0 && <p className={styles.helperText}>لا توجد أقسام أو محطات منشورة.</p>}
            {sectionOptions.map((section) => (
              <label key={section.id} className={styles.checkboxField}>
                <input
                  type="checkbox"
                  checked={builder.resolvedSectionIds.has(section.id)}
                  onChange={() => builder.toggleSection(section.id)}
                />
                <span>{section.name}</span>
                <small>{content.stations.filter((station) => station.sectionId === section.id && station.status === 'published').length}</small>
              </label>
            ))}
          </div>
        </fieldset>

        <div className={styles.fieldRow}>
          <label htmlFor="exam-count">عدد المحطات</label>
          <input
            id="exam-count"
            type="number"
            min={builder.maxCount > 0 ? 1 : 0}
            max={builder.maxCount}
            value={builder.count === 0 ? '' : builder.count}
            onChange={(event) => builder.setCount(Number(event.target.value))}
            disabled={!builder.hasAvailableStations}
            aria-describedby="exam-count-help"
          />
          <small id="exam-count-help">{builder.maxCount > 0 ? `المتاح حاليًا: ${builder.maxCount} محطة` : 'لا توجد محطات منشورة متاحة'}</small>
        </div>

        <label className={styles.switchRow}>
          <input type="checkbox" checked={builder.imagesOnly} onChange={(event) => builder.setImagesOnly(event.target.checked)} />
          <span>محطات تحتوي على صور فقط</span>
          <ImageIcon size={16} aria-hidden="true" />
        </label>

        <label className={styles.switchRow}>
          <input type="checkbox" checked={builder.shuffle} onChange={(event) => builder.setShuffle(event.target.checked)} />
          <span>ترتيب المحطات عشوائيًا</span>
          <Shuffle size={16} aria-hidden="true" />
        </label>

        <button type="button" className={styles.primaryButton} onClick={startExam} disabled={!builder.hasAvailableStations || builder.safeCount < 1}>
          <Play size={16} aria-hidden="true" /> بدء الاختبار
        </button>
        <p className={styles.helperText}>ستظهر أسئلة كل محطة وإجاباتها داخل الاختبار.</p>
      </section>
    </main>
  )
}

export { ExamRoute as default }
